import React, { useRef, useState } from "react";
import { JSONContent, NodeViewWrapper } from "@tiptap/react";
import { useFetchContext } from "../../../../common/FetchContext";

import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import RefreshIcon from "@material-ui/icons/Refresh";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Height } from "@material-ui/icons";
import { bindSummaryTable } from "./summaryTableBinding";
import { bindPerformanceSections } from "./performanceTableBinding";
import { isFeStageImageTable } from "./feImageTableBinding";
import {
  normalizeKey,
  buildReportDetailsFieldAliases,
  isReportDetailsTemplate as checkIsReportDetailsTemplate,
  buildReportApprovalFieldAliases,
  isReportApprovalTemplate as checkIsReportApprovalTemplate,
} from "./reportInfoFieldAliases";

export default function LSDBButtonView(props: any) {
  const { authAxios } = useFetchContext();
  const { editor, getPos, node } = props;

  const [loading, setLoading] = useState<boolean>(false);
  const originalTableRef = useRef<any>(null);
  // `loading` (React state) is only reflected in the DOM/closures after the
  // next render, so a rapid double-click can fire handleClick twice before
  // either sees `loading === true` — both calls then proceed to fetch data
  // and independently compute the same insertion range from the
  // not-yet-edited document, so the second insertContentAt lands on stale
  // coordinates and creates a visible duplicate table instead of replacing
  // the same one. A ref is updated synchronously, so checking it guards
  // against that race regardless of render timing.
  const isFetchingRef = useRef(false);

  const alignment = node?.attrs?.alignment || "right";

  /**
   * Helper to recursively extract plain text from a TipTap AST node.
   */
  const extractTextFromNode = (node: any): string => {
    if (!node) return "";
    if (node.type === "text") return node.text || "";
    if (Array.isArray(node.content)) {
      const isInline = node.type === "paragraph" || node.type === "heading";
      return node.content.map(extractTextFromNode).join(isInline ? "" : " ");
    }
    return "";
  };

  const getRowTexts = (row: any): string[] => {
    return (row.content || []).map((cell: any) =>
      extractTextFromNode(cell).trim(),
    );
  };

  const formatImageSrc = (imgStr: string): string => {
    if (!imgStr) return "";
    const cleanStr = imgStr.replace(/\s+/g, "");
    if (cleanStr.startsWith("data:image/") || cleanStr.startsWith("http")) {
      return cleanStr;
    }
    if (cleanStr.startsWith("iVBOR")) {
      return `data:image/png;base64,${cleanStr}`;
    }
    if (cleanStr.startsWith("R0lGOD")) {
      return `data:image/gif;base64,${cleanStr}`;
    }
    return `data:image/jpeg;base64,${cleanStr}`;
  };

  /**
   * Formats a plain measurement object's own fields into one readable line,
   * e.g. { position: 1, L: 40.83, a: 1.83, b: -13 } -> "1: L=40.83, a=1.83,
   * b=-13". A "position"/"index"/"label"/"name" style field (if present) is
   * used as a leading label; every other field is appended as "key=value",
   * with numeric values rounded to 2 decimals for readability.
   */
  const formatMeasurementObject = (obj: Record<string, any>): string => {
    const labelKeys = ["position", "index", "label", "name", "point"];
    const labelKey = labelKeys.find(
      (k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== "",
    );
    const label = labelKey ? String(obj[labelKey]) : null;

    const parts = Object.entries(obj)
      .filter(([k]) => k !== labelKey)
      .map(([k, v]) => {
        if (v === null || v === undefined || v === "") return null;
        const num = Number(v);
        const formattedVal =
          typeof v !== "boolean" && !isNaN(num)
            ? Number.isInteger(num)
              ? String(num)
              : num.toFixed(2)
            : String(v);
        return `${k}=${formattedVal}`;
      })
      .filter((part): part is string => part !== null);

    return label ? `${label}: ${parts.join(", ")}` : parts.join(", ");
  };

  const formatRawValue = (
    val: any,
  ): {
    isImage: boolean;
    value: string;
    rawImages: string[];
    // One entry per array element when `val` was an array of plain
    // measurement objects, so callers can render one paragraph/line per
    // reading instead of a single run-on string. Absent for simple values.
    lines?: string[];
  } => {
    if (val === null || val === undefined) {
      return { isImage: false, value: "", rawImages: [] };
    }

    const isImgStr = (item: any) => {
      if (typeof item !== "string") return false;
      const cleanItem = item.replace(/\s+/g, "");
      return (
        cleanItem.startsWith("http") ||
        cleanItem.startsWith("data:image") ||
        cleanItem.startsWith("/9j/") ||
        cleanItem.startsWith("iVBOR") ||
        cleanItem.startsWith("R0lGOD") ||
        cleanItem.startsWith("UklGR") ||
        cleanItem.length > 500
      );
    };

    if (Array.isArray(val)) {
      const validItems = val.filter(
        (item) => item !== null && item !== undefined && item !== "",
      );
      if (validItems.length === 0)
        return { isImage: false, value: "", rawImages: [] };

      const isImgArray = validItems.some(isImgStr);
      if (isImgArray) {
        return {
          isImage: true,
          value: "",
          rawImages: validItems.map((img) => formatImageSrc(img)),
        };
      }

      // Arrays of plain measurement objects (e.g. LSDB's per-position color
      // readings: [{position:1, L:.., a:.., b:..}, ...]) used to be joined
      // via Array.prototype.join, which calls the default
      // Object.prototype.toString on each element — rendering
      // "[object Object]" once per entry instead of the actual data. Format
      // each object into a readable summary line instead, and expose them
      // as `lines` so the caller can render one paragraph per reading.
      const isObjectArray = validItems.some(
        (item) => typeof item === "object" && item !== null,
      );
      if (isObjectArray) {
        const lines = validItems.map((item) =>
          typeof item === "object" && item !== null
            ? formatMeasurementObject(item)
            : String(item),
        );
        return {
          isImage: false,
          value: lines.join("; "),
          rawImages: [],
          lines,
        };
      }

      return { isImage: false, value: validItems.join(", "), rawImages: [] };
    }

    if (isImgStr(val)) {
      return { isImage: true, value: "", rawImages: [formatImageSrc(val)] };
    }

    return { isImage: false, value: String(val), rawImages: [] };
  };

  const getDynamicCellValue = (
    headerText: string,
    apiItem: Record<string, any>,
    colIndex: number,
  ) => {
    if (typeof apiItem !== "object" || apiItem === null) {
      return { isImage: false, value: "", rawImages: [] };
    }

    // STC endpoints nest measurements while keeping module identity at the top level.
    const measurements = apiItem.measurement_result;
    const fields =
      measurements !== null &&
      typeof measurements === "object" &&
      !Array.isArray(measurements)
        ? { ...apiItem, ...measurements }
        : apiItem;
    const cleanHeader = normalizeKey(headerText);
    const apiKeys = Object.keys(fields).filter(
      (key) => key !== "measurement_result",
    );

    const keyAliases: Record<string, string[]> = {
      serialnumber: ["serialnumber", "serial", "serial#", "model"],
      findings: ["findings", "finding", "observation", "notes"],
      defectcatagory: ["defectcatagory", "defectcategory", "category"],
      image: ["image", "images", "photo", "photos", "blob_url"],
      pmax: ["pmp", "pmax", "power"],
      voc: ["voc"],
      vmp: ["vmp", "voltage"],
      isc: ["isc"],
      imp: ["imp", "current"],
      ff: ["ff", "fillfactor"],
      insulationresistance: [
        "insulationresistance",
        "insulation",
        "resistance",
        "ir",
      ],
      withiniec61215srequirement: [
        "iec_requirement",
        "iec",
        "withiniec",
        "meetsiec",
        "requirement",
      ],
      withiniec61215requirement: [
        "iec_requirement",
        "iec",
        "withiniec",
        "meetsiec",
        "requirement",
      ],
      // Add the alias for absolute deviation
      absolutedeviationfromdatasheetsnominalvalue: [
        "absolutedeviation",
        "deviation",
      ],
    };

    let targetKey = "";

    if (cleanHeader) {
      targetKey = apiKeys.find((k) => normalizeKey(k) === cleanHeader) || "";
      if (!targetKey) {
        const aliases = keyAliases[cleanHeader] || [];
        targetKey =
          apiKeys.find((k) => aliases.includes(normalizeKey(k))) || "";
      }
      if (!targetKey) {
        targetKey =
          apiKeys.find((k) => {
            const cleanK = normalizeKey(k);
            return cleanK.includes(cleanHeader) || cleanHeader.includes(cleanK);
          }) || "";
      }
      if (!targetKey && cleanHeader.includes("iec")) {
        targetKey =
          apiKeys.find(
            (k) =>
              normalizeKey(k).includes("iec") ||
              normalizeKey(k).includes("requirement"),
          ) || "";
      }
    }

    if (!targetKey && fields === apiItem && apiKeys[colIndex]) {
      targetKey = apiKeys[colIndex];
    }

    const rawVal = targetKey ? fields[targetKey] : "";
    return formatRawValue(rawVal);
  };

  const handleClick = async () => {
    if (loading || isFetchingRef.current) return;
    isFetchingRef.current = true;

    const { from } = editor.state.selection;

    try {
      if (!editor || typeof getPos !== "function") {
        console.error("Editor instance or getPos is missing.");
        return;
      }

      const url = node?.attrs?.url;
      if (!url) {
        alert("Error: No API URL specified on button attributes.");
        return;
      }

      setLoading(true);
      const res = await authAxios.get(url);
      const rawItems = Array.isArray(res.data)
        ? res.data
        : res.data?.data || res.data?.items || [res.data];

      if (!rawItems || rawItems.length === 0) {
        alert("No data records returned from API.");
        setLoading(false);
        return;
      }

      if (node?.attrs?.is_image) {
        const currentPos = getPos();
        const { doc } = editor.state;

        let nextNode: any = null;
        let nextNodePos = -1;

        // 1. Find the table directly below the button
        // FIX: Limit the search radius to 50 positions to prevent grabbing tables in other sections
        const searchEndPos = Math.min(currentPos + 50, doc.content.size);

        doc.nodesBetween(currentPos, searchEndPos, (n: any, pos: number) => {
          if (pos <= currentPos) return;
          if (!nextNode && n.type.name === "table") {
            nextNode = n;
            nextNodePos = pos;
            return false;
          }
        });

        let targetPos = -1;

        // 2. Find the inside of the FIRST cell in the SECOND row
        if (nextNode) {
          let rowCount = 0;

          doc.nodesBetween(
            nextNodePos,
            nextNodePos + nextNode.nodeSize,
            (n: any, pos: number) => {
              if (n.type.name === "tableRow") {
                rowCount++;
              }

              // Target the "tableCell" directly, because empty cells might not have a paragraph yet
              if (
                n.type.name === "tableCell" &&
                rowCount === 2 &&
                targetPos === -1
              ) {
                targetPos = pos + 1; // +1 puts the coordinate perfectly inside the table cell
              }
            },
          );
        }
        const afterButtonPos = currentPos + node.nodeSize;
        // 3. Dispatch the event, passing the EXACT cell coordinate (or defaulting to 'from' if it fails)
        window.dispatchEvent(
          new CustomEvent("open-image-drawer", {
            detail: {
              is_image: true,
              url,
              images: rawItems,
              insertPosition: targetPos !== -1 ? targetPos : afterButtonPos,
              targetEditor: editor,
              feImageTablePosition: nextNode && isFeStageImageTable(nextNode.toJSON())
                ? nextNodePos : undefined,
            },
          }),
        );

        setLoading(false);
        return;
      }

      const currentPos = getPos();
      const { doc } = editor.state;

      let nextNode: any = null;
      let nextNodePos = -1;

      doc.descendants((node: any, pos: number) => {
        if (pos <= currentPos) return;
        if (!nextNode && node.type.name === "table") {
          nextNode = node;
          nextNodePos = pos;
          return false;
        }
      });

      if (!nextNode) {
        alert("No table found after this button.");
        setLoading(false);
        return;
      }

      const tableJson = nextNode.toJSON();
      const existingRows = tableJson.content || [];

      if (!existingRows.length) {
        alert("Target table has zero rows.");
        setLoading(false);
        return;
      }

      if (!originalTableRef.current) {
        originalTableRef.current = JSON.parse(JSON.stringify(existingRows));
      }
      const templateRows = originalTableRef.current;

      const identificationHeaderIndex = existingRows.findIndex((row: JSONContent) => {
        const headers = getRowTexts(row).map(normalizeKey);
        return headers.includes("designation") && headers.includes("serialnumber");
      });
      const hasFixedModuleRows = existingRows.some((row: JSONContent) =>
        getRowTexts(row).some((text) => /^module\s+\d+$/i.test(text.trim())),
      );
      if (identificationHeaderIndex !== -1 && hasFixedModuleRows) {
        const headers = getRowTexts(existingRows[identificationHeaderIndex]).map(normalizeKey);
        const designationColumn = headers.indexOf("designation");
        const serialColumn = headers.indexOf("serialnumber");
        const records: Record<string, unknown>[] = rawItems;
        const feRecords = records.filter((item) =>
          typeof item.designation === "string" && normalizeKey(item.designation) === "fe",
        );
        const controlRecords = records.filter((item) =>
          typeof item.designation === "string" && normalizeKey(item.designation) === "control",
        );
        const usedRecords = new Set<Record<string, unknown>>();
        const updatedRows = existingRows.map((row: JSONContent, rowIndex: number) => {
          if (rowIndex <= identificationHeaderIndex) return row;
          const designation = extractTextFromNode(row.content?.[designationColumn]).trim();
          const moduleMatch = designation.match(/^module\s+(\d+)$/i);
          const isControl = normalizeKey(designation) === "control";
          if (!moduleMatch && !isControl) return row;
          const record = isControl
            ? controlRecords[0]
            : feRecords[Number(moduleMatch?.[1]) - 1];
          if (!record || record.serial_number == null || record.serial_number === "") {
            throw new Error(`No serial number returned for identification row "${designation}".`);
          }
          const serialCell = row.content?.[serialColumn];
          if (!serialCell) {
            throw new Error(`No serial-number cell found for identification row "${designation}".`);
          }
          usedRecords.add(record);
          const content = [...(row.content || [])];
          content[serialColumn] = {
            ...serialCell,
            content: [{
              type: "paragraph",
              attrs: serialCell.content?.[0]?.attrs,
              content: [{ type: "text", text: String(record.serial_number) }],
            }],
          };
          return { ...row, content };
        });
        if (usedRecords.size !== records.length) {
          throw new Error("The module identification table does not have matching rows for every returned module.");
        }
        editor.chain().focus().insertContentAt(
          { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
          { ...tableJson, content: updatedRows },
        ).run();
        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 1: Report Details (Key-Value Grid)
      // ─────────────────────────────────────────────────────────────
      // Detection and field mapping are centralized in
      // reportInfoFieldAliases.ts so the same logic (and its label
      // normalization) is shared, tested, and easy to extend for new
      // Report Types without touching this component.
      const isReportDetailsTemplate = checkIsReportDetailsTemplate(
        existingRows.map((row: any) => getRowTexts(row)),
      );

      if (isReportDetailsTemplate) {
        const apiItem = rawItems[0] || {};

        // Keyed by normalizeKey() (not the raw label) so labels coming from
        // Word-doc-derived templates — which often carry trailing periods,
        // colons, or odd spacing (e.g. "Project Number.") — still match
        // instead of silently failing an exact-string lookup and leaving
        // the cell blank.
        const keyMapping = buildReportDetailsFieldAliases(apiItem);

        const updatedRows = existingRows.map((row: any) => {
          const newRow = JSON.parse(JSON.stringify(row));
          if (newRow.content && Array.isArray(newRow.content)) {
            for (let i = 0; i < newRow.content.length - 1; i++) {
              const cellText = extractTextFromNode(newRow.content[i]);
              const cleanLabel = normalizeKey(cellText);

              if (keyMapping[cleanLabel] !== undefined) {
                const val = keyMapping[cleanLabel];
                // Insert "N/A" rather than leaving the cell blank when the
                // API genuinely has no value, so a completed fetch is
                // visually distinguishable from a field that was never
                // fetched at all.
                newRow.content[i + 1].content = [
                  {
                    type: "paragraph",
                    content: [
                      { type: "text", text: val ? String(val) : "N/A" },
                    ],
                  },
                ];
              }
            }
          }
          return newRow;
        });

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            { type: "table", content: updatedRows },
          )
          .run();
        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 2: Report Approval (Preparation/Verification)
      // ─────────────────────────────────────────────────────────────
      const fullTableText = existingRows
        .map((r: any) => getRowTexts(r).join(" ").toLowerCase())
        .join(" ");
      const isReportApprovalTemplate =
        checkIsReportApprovalTemplate(fullTableText);

      if (isReportApprovalTemplate) {
        const apiItem = rawItems[0] || {};

        const approvalMapping = buildReportApprovalFieldAliases(apiItem);

        // Dynamically find the "Name" column in case the layout shifts
        const headerTexts = getRowTexts(existingRows[0]).map((t) =>
          t.toLowerCase().trim(),
        );
        let nameColIdx = headerTexts.indexOf("name");
        if (nameColIdx === -1) nameColIdx = 1; // Default to second column if explicit header is missing

        const updatedRows = existingRows.map((row: any) => {
          const newRow = JSON.parse(JSON.stringify(row));

          if (newRow.content && newRow.content.length > nameColIdx) {
            const firstCellText = extractTextFromNode(newRow.content[0]);
            const cleanLabel = normalizeKey(firstCellText);

            if (approvalMapping[cleanLabel] !== undefined) {
              const val = approvalMapping[cleanLabel];
              newRow.content[nameColIdx].content = [
                {
                  type: "paragraph",
                  content: [{ type: "text", text: val ? String(val) : "N/A" }],
                },
              ];
            }
          }
          return newRow;
        });

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            { type: "table", content: updatedRows },
          )
          .run();

        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 3: Summary/Disclaimer Grid
      // ─────────────────────────────────────────────────────────────
      const isSummaryDisclaimerTemplate = existingRows.some((row: any) => {
        const text = getRowTexts(row).join(" ").toLowerCase();
        return (
          text.includes("visual inspection") ||
          text.includes("wet leakage") ||
          text.includes("electrical circuitry") ||
          text.includes("average delta")
        );
      });

      if (isSummaryDisclaimerTemplate) {
        const apiItem = rawItems[0] || {};
        const updatedRows = bindSummaryTable(existingRows, apiItem);
        if (!updatedRows) {
          alert("No matching summary fields found in the target table.");
          setLoading(false);
          return;
        }

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            { ...tableJson, content: updatedRows },
          )
          .run();

        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 4: Performance Data Grid
      // ─────────────────────────────────────────────────────────────
      const isPerformanceGridTemplate = existingRows.some((row: any) => {
        const text = getRowTexts(row).join(" ").toLowerCase();
        // Added "bifaciality" to ensure this specific table reliably triggers
        return (
          text.includes("pmax") ||
          text.includes("voc") ||
          text.includes("isc") ||
          text.includes("bifaciality")
        );
      });

      if (isPerformanceGridTemplate) {
        const sectionRows = bindPerformanceSections(existingRows, rawItems);
        if (sectionRows) {
          editor.chain().focus().insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            { ...tableJson, content: sectionRows },
          ).run();
          setLoading(false);
          return;
        }
        const headerRows: any[] = [];
        const footerRows: any[] = [];
        let headerTexts: string[] = [];
        let dataRowTemplate: any = null;
        let dataRowIndex = -1;

        // 1. First pass: Identify the data template row and footer rows
        templateRows.forEach((row: any, index: number) => {
          const rowText = getRowTexts(row).join(" ").toUpperCase();
          if (
            rowText.includes("<SERIAL#") ||
            rowText.includes("<MODULE") ||
            (rowText.includes("<") && rowText.includes(">"))
          ) {
            if (!dataRowTemplate) {
              dataRowTemplate = row;
              dataRowIndex = index; // Mark exactly where the data starts
            }
          } else if (
            rowText.includes("AVERAGE") ||
            rowText.includes("SUMMARY") ||
            rowText.includes("STDV")
          ) {
            footerRows.push(row);
          }
        });

        // 2. Second pass: Extract headers precisely
        if (dataRowIndex > 0) {
          // The row right before the data template is our true column header
          headerTexts = getRowTexts(templateRows[dataRowIndex - 1]);

          // Everything before the data row is a header row (preserves super-headers)
          for (let i = 0; i < dataRowIndex; i++) {
            headerRows.push(templateRows[i]);
          }
        } else {
          // Fallback just in case
          headerTexts = getRowTexts(templateRows[0]);
          headerRows.push(templateRows[0]);
        }

        const columnCount = headerTexts.length || 7;
        const SERIAL_COL_WIDTH = [250];

        // 1. Generate Data Rows with adjusted colwidth
        const generatedDataRows = rawItems.map((apiItem: any, idx: number) => {
          const cells = Array.from({ length: columnCount }).map(
            (_, colIndex) => {
              const headerText = headerTexts[colIndex] || "";
              let { value, lines } = getDynamicCellValue(
                headerText,
                apiItem,
                colIndex,
              );

              if (
                normalizeKey(headerText).includes("serial") &&
                (!value || value === "N/A" || value === "")
              ) {
                value =
                  apiItem.serial_number ||
                  apiItem.serialnumber ||
                  `Serial #${idx + 1}`;
              }

              let finalCellText = value;
              if (!finalCellText || finalCellText === "null") {
                finalCellText = "";
              } else {
                const num = Number(finalCellText);
                if (!isNaN(num) && !Number.isInteger(num)) {
                  finalCellText = num.toFixed(2);
                }
              }

              let cellAttrs = dataRowTemplate?.content?.[colIndex]?.attrs || {
                colspan: 1,
                rowspan: 1,
                colwidth: null,
              };

              // ✅ INCREASE WIDTH FOR SERIAL NUMBER COLUMN
              if (normalizeKey(headerText).includes("serial")) {
                cellAttrs = { ...cellAttrs, colwidth: SERIAL_COL_WIDTH };
              }

              // Arrays of measurement objects (e.g. multiple readings per
              // module) come back as `lines` — one formatted summary per
              // reading — instead of a single joined string that would
              // otherwise render as "[object Object]" repeated. Render each
              // as its own paragraph within the cell.
              if (lines && lines.length > 0) {
                return {
                  type: "tableCell",
                  attrs: cellAttrs,
                  content: lines.map((line) => ({
                    type: "paragraph",
                    content: [{ type: "text", text: line }],
                  })),
                };
              }

              return {
                type: "tableCell",
                attrs: cellAttrs,
                content: [
                  {
                    type: "paragraph",
                    content: finalCellText
                      ? [{ type: "text", text: String(finalCellText) }]
                      : [],
                  },
                ],
              };
            },
          );

          return { type: "tableRow", content: cells };
        });

        // 2. Adjust Header Rows colwidth to match
        const updatedHeaderRows = headerRows.map((row: any) => {
          const newRow = JSON.parse(JSON.stringify(row));
          newRow.content?.forEach((cell: any, colIndex: number) => {
            const headerText = headerTexts[colIndex] || "";
            if (normalizeKey(headerText).includes("serial")) {
              cell.attrs = { ...cell.attrs, colwidth: SERIAL_COL_WIDTH };
            }
          });
          return newRow;
        });

        // 3. Adjust Footer Rows colwidth to match
        const calculatedFooterRows = footerRows.map((row: any) => {
          const newRow = JSON.parse(JSON.stringify(row));
          const rowText = getRowTexts(row).join(" ").toUpperCase();

          if (rowText.includes("AVERAGE") && newRow.content) {
            newRow.content.forEach((cell: any, colIndex: number) => {
              // ✅ Apply width to footer serial column
              const headerText = headerTexts[colIndex] || "";
              if (normalizeKey(headerText).includes("serial")) {
                cell.attrs = { ...cell.attrs, colwidth: SERIAL_COL_WIDTH };
              }

              if (colIndex === 0) return;

              let sum = 0;
              let count = 0;

              rawItems.forEach((item: any) => {
                const { value } = getDynamicCellValue(
                  headerText,
                  item,
                  colIndex,
                );
                const num = parseFloat(value);
                if (!isNaN(num)) {
                  sum += num;
                  count++;
                }
              });

              if (count > 0) {
                const avg = (sum / count).toFixed(2);
                cell.content = [
                  {
                    type: "paragraph",
                    content: [{ type: "text", text: String(avg) }],
                  },
                ];
              }
            });
          }
          return newRow;
        });

        const completeTableNode = {
          type: "table",
          // Make sure to use `updatedHeaderRows` here instead of `headerRows`
          content: [
            ...updatedHeaderRows,
            ...generatedDataRows,
            ...calculatedFooterRows,
          ],
        };

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            completeTableNode,
          )
          .run();
        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 5: (<Serial#01> and Images stacked)
      // ─────────────────────────────────────────────────────────────
      const isStackedSerialTemplate = existingRows.some(
        (row: any, idx: number) => {
          const texts = getRowTexts(row);
          return texts.join(" ").includes("<Serial#01>") && texts.length === 1;
        },
      );

      if (isStackedSerialTemplate) {
        const generatedRows: any[] = [];
        rawItems.forEach((item: any, idx: number) => {
          generatedRows.push({
            type: "tableRow",
            content: [
              {
                type: "tableCell",
                attrs: { colspan: 1, rowspan: 1, colwidth: null },
                content: [
                  {
                    type: "paragraph",
                    attrs: { textAlign: "center" },
                    content: [
                      {
                        type: "text",
                        text: `Serial Number: ${item.serial_number || item.serialnumber || idx + 1}`,
                      },
                    ],
                  },
                ],
              },
            ],
          });

          const imageVal =
            item.image || item.images || item.photo || item.photos;
          const images = Array.isArray(imageVal)
            ? imageVal
            : imageVal
              ? [imageVal]
              : [];
          const imageParagraphs = images.map((img: string) => ({
            type: "paragraph",
            content: [
              {
                type: "image",
                attrs: {
                  src: formatImageSrc(img),
                  width: 300,
                  height: "200",
                  alt: "Serial Image",
                  title: null,
                },
              },
            ],
          }));

          generatedRows.push({
            type: "tableRow",
            content: [
              {
                type: "tableCell",
                attrs: { colspan: 1, rowspan: 1, colwidth: null },
                // FIXED: Schema-safe empty paragraph so TipTap doesn't crash the render
                content:
                  imageParagraphs.length > 0
                    ? imageParagraphs
                    : [
                        {
                          type: "paragraph",
                          content: [{ type: "text", text: " " }],
                        },
                      ],
              },
            ],
          });
        });

        // FIXED: Safely delete the old table first, then insert the new array.
        // This avoids complex coordinate math and bypasses the array replacement bug.
        editor
          .chain()
          .focus()
          .deleteRange({
            from: nextNodePos,
            to: nextNodePos + nextNode.nodeSize,
          })
          .insertContentAt(nextNodePos, {
            type: "table",
            content: generatedRows,
          })
          .run();

        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 6: Inspection Findings Grid
      // ─────────────────────────────────────────────────────────────
      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 6: Inspection Findings Grid
      // ─────────────────────────────────────────────────────────────
      const isInspectionFindingsTemplate = existingRows.some((row: any) => {
        const text = getRowTexts(row).join(" ").toLowerCase();
        return text.includes("findings") && text.includes("defect category");
      });

      if (isInspectionFindingsTemplate) {
        const headerRows: any[] = [];
        let headerTexts: string[] = [];
        let dataRowTemplate: any = null;
        let hasFoundHeaders = false;

        existingRows.forEach((row: any) => {
          const rowText = getRowTexts(row).join(" ").toUpperCase();
          if (rowText.includes("<SERIAL#") || hasFoundHeaders) {
            if (!dataRowTemplate) dataRowTemplate = row;
          } else {
            headerRows.push(row);
            if (
              rowText.includes("FINDINGS") ||
              rowText.includes("DEFECT CATEGORY")
            ) {
              headerTexts = getRowTexts(row);
              hasFoundHeaders = true;
            }
          }
        });

        if (headerTexts.length === 0 && headerRows.length > 0) {
          headerTexts = getRowTexts(headerRows[headerRows.length - 1]);
        }

        const columnCount = headerTexts.length || 4;
        const imagesToAppend: any[] = []; // Array to collect images for outside the table

        const generatedDataRows = rawItems.map((apiItem: any, idx: number) => {
          const cells = Array.from({ length: columnCount }).map(
            (_, colIndex) => {
              const headerText = headerTexts[colIndex] || "";
              let { isImage, value, rawImages } = getDynamicCellValue(
                headerText,
                apiItem,
                colIndex,
              );

              const originalCellAttrs = dataRowTemplate?.content?.[colIndex]
                ?.attrs || {
                colspan: 1,
                rowspan: 1,
                colwidth: null,
              };
              const instructionText = extractTextFromNode(
                dataRowTemplate?.content?.[colIndex] || {},
              );

              if (
                normalizeKey(headerText).includes("serial") &&
                (!value || value === "N/A" || value === "")
              ) {
                value =
                  apiItem.serial_number ||
                  apiItem.serialnumber ||
                  `Serial #${idx + 1}`;
              }

              // --- 1. NEW LOGIC: Handle new JSON structure (is_image and img_data) ---
              const isImageCol =
                normalizeKey(headerText).includes("image") ||
                normalizeKey(headerText).includes("photo");

              if (isImageCol && apiItem.is_image) {
                const imgDataArray = Array.isArray(apiItem.img_data)
                  ? apiItem.img_data
                  : [];

                // (The auto-append loop was deleted from here!)

                // Return "Select Image" hyperlink style for the cell that triggers the side drawer
                return {
                  type: "tableCell",
                  attrs: originalCellAttrs,
                  content: [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "text",
                          text: "Select Image",
                          marks: [
                            { type: "underline" },
                            { type: "textStyle", attrs: { color: "#1976D2" } },
                            {
                              type: "link",
                              attrs: {
                                "data-action": "open-image-drawer",
                                "data-images": JSON.stringify(imgDataArray),
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                };
              }

              // --- 2. LEGACY LOGIC: Fallback for older data structures ---
              if (isImage && rawImages.length > 0) {
                const imageName =
                  apiItem.image_name || apiItem.imagename || "Attached Below";

                rawImages.forEach((imgUrl) => {
                  imagesToAppend.push({
                    type: "paragraph",
                    content: [{ type: "text", text: " " }],
                  });

                  const imageParagraphs = [
                    {
                      type: "paragraph",
                      content: [
                        {
                          type: "image",
                          attrs: {
                            src: imgUrl,
                            width: 300,
                            height: 200,
                          },
                        },
                      ],
                    },
                  ];

                  imagesToAppend.push({
                    type: "table",
                    content: [
                      {
                        type: "tableRow",
                        content: [
                          {
                            type: "tableHeader",
                            attrs: { colspan: 1, rowspan: 1, colwidth: null },
                            content: [
                              {
                                type: "paragraph",
                                attrs: { textAlign: "center" },
                                content: [
                                  { type: "text", text: `Image: ${imageName}` },
                                ],
                              },
                            ],
                          },
                        ],
                      },
                      {
                        type: "tableRow",
                        content: [
                          {
                            type: "tableCell",
                            attrs: { colspan: 1, rowspan: 1, colwidth: null },
                            content: imageParagraphs,
                          },
                        ],
                      },
                    ],
                  });
                });

                return {
                  type: "tableCell",
                  attrs: originalCellAttrs,
                  content: [
                    {
                      type: "paragraph",
                      content: [{ type: "text", text: String(imageName) }],
                    },
                  ],
                };
              }

              let finalCellText = value;
              if (
                !finalCellText ||
                finalCellText === "N/A" ||
                finalCellText === "null"
              ) {
                if (instructionText.includes("Not Applicable")) {
                  finalCellText = "Not Applicable";
                } else if (instructionText.includes("None")) {
                  finalCellText = "None";
                } else {
                  finalCellText = "None";
                }
              }

              // Handle `is_image: false` by printing "Not Applicable" or the fallback text
              if (isImageCol && apiItem.is_image === false) {
                finalCellText = "Not Applicable";
              }

              return {
                type: "tableCell",
                attrs: originalCellAttrs,
                content: [
                  {
                    type: "paragraph",
                    content: finalCellText
                      ? [{ type: "text", text: String(finalCellText) }]
                      : [],
                  },
                ],
              };
            },
          );

          return { type: "tableRow", content: cells };
        });

        const completeTableNode = {
          type: "table",
          content: [...headerRows, ...generatedDataRows],
        };

        const contentToInsert = [completeTableNode, ...imagesToAppend];

        // Safely delete the old table first, then insert the new array at that exact position.
        editor
          .chain()
          .focus()
          .deleteRange({
            from: nextNodePos,
            to: nextNodePos + nextNode.nodeSize,
          })
          .insertContentAt(nextNodePos, contentToInsert)
          .run();

        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 7: Insulation Resistance Grid
      // ─────────────────────────────────────────────────────────────
      const isInsulationResistanceTemplate = existingRows.some((row: any) => {
        const text = getRowTexts(row).join(" ").toLowerCase();
        return text.includes("insulation resistance") && text.includes("iec");
      });

      if (isInsulationResistanceTemplate) {
        const headerIndex = existingRows.findIndex((row: JSONContent) => {
          const texts = getRowTexts(row).map(normalizeKey);
          return texts.some((text) => text.includes("insulationresistance")) &&
            texts.some((text) => text.includes("iec"));
        });
        if (headerIndex === -1) {
          throw new Error("No column headers found in the insulation resistance table.");
        }
        const headerRows = existingRows.slice(0, headerIndex + 1);
        const headerTexts = getRowTexts(existingRows[headerIndex]);
        const dataRowTemplate = existingRows[headerIndex + 1];
        const columnCount = headerTexts.length;

        const generatedDataRows = rawItems.map((apiItem: any, idx: number) => {
          const cells = Array.from({ length: columnCount }).map(
            (_, colIndex) => {
              const headerText = headerTexts[colIndex] || "";
              let { value } = getDynamicCellValue(
                headerText,
                apiItem,
                colIndex,
              );

              const originalCellAttrs = dataRowTemplate?.content?.[colIndex]
                ?.attrs || {
                colspan: 1,
                rowspan: 1,
                colwidth: null,
              };
              const instructionText = extractTextFromNode(
                dataRowTemplate?.content?.[colIndex] || {},
              );

              if (
                normalizeKey(headerText).includes("serial") &&
                (!value || value === "N/A" || value === "")
              ) {
                value =
                  apiItem.serial_number ||
                  apiItem.serialnumber ||
                  `Serial #${idx + 1}`;
              }

              let finalCellText = value;
              if (normalizeKey(headerText).includes("iec") && finalCellText) {
                const lowerVal = String(finalCellText).toLowerCase();
                if (
                  lowerVal === "true" ||
                  lowerVal === "yes" ||
                  lowerVal === "pass"
                ) {
                  finalCellText = "Yes";
                } else if (
                  lowerVal === "false" ||
                  lowerVal === "no" ||
                  lowerVal === "fail"
                ) {
                  finalCellText = "No";
                }
              }

              if (
                !finalCellText ||
                finalCellText === "N/A" ||
                finalCellText === "null"
              ) {
                if (
                  instructionText.includes("Yes/No") ||
                  instructionText.includes("Yes / No")
                ) {
                  finalCellText = "N/A";
                } else {
                  finalCellText = "";
                }
              }

              return {
                type: "tableCell",
                attrs: originalCellAttrs,
                content: [
                  {
                    type: "paragraph",
                    attrs: dataRowTemplate?.content?.[colIndex]?.content?.[0]?.attrs,
                    content: finalCellText
                      ? [{ type: "text", text: String(finalCellText) }]
                      : [],
                  },
                ],
              };
            },
          );

          return { type: "tableRow", content: cells };
        });

        const completeTableNode = {
          ...tableJson,
          content: [...headerRows, ...generatedDataRows],
        };

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            completeTableNode,
          )
          .run();
        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // TEMPLATE LOGIC 8: Grouped Color Measurement Grid
      // (Position | L* | a* | b*) columns repeated once per serial-number
      // "group" (e.g. two side-by-side groups for two modules), with a
      // super-header row naming each group and a sub-header row naming
      // the Position/L*/a*/b* columns within every group.
      // ─────────────────────────────────────────────────────────────
      const superHeaderRow = existingRows[0];
      const subHeaderRow = existingRows[1];
      const superHeaderJoined = superHeaderRow
        ? getRowTexts(superHeaderRow).join(" ").toLowerCase()
        : "";
      const subHeaderTexts = subHeaderRow ? getRowTexts(subHeaderRow) : [];
      const subHeaderJoined = subHeaderTexts.join(" ").toLowerCase();
      const isColorMeasurementGridTemplate =
        superHeaderJoined.includes("serial") &&
        subHeaderJoined.includes("position") &&
        subHeaderJoined.includes("l*") &&
        subHeaderJoined.includes("a*") &&
        subHeaderJoined.includes("b*");

      if (isColorMeasurementGridTemplate) {
        const numGroups = superHeaderRow?.content?.length || 1;
        const totalColumns =
          subHeaderRow?.content?.length || subHeaderTexts.length;
        const columnsPerGroup =
          numGroups > 0 ? Math.round(totalColumns / numGroups) : totalColumns;

        // Figure out, within one group's local columns, which column holds
        // Position / L* / a* / b* — rather than assuming a fixed order —
        // so this still works if a report type reorders these slightly.
        const localHeaderTexts = subHeaderTexts.slice(0, columnsPerGroup);
        const findLocalCol = (matcher: (t: string) => boolean) => {
          const idx = localHeaderTexts.findIndex((t) =>
            matcher(t.toLowerCase().trim()),
          );
          return idx === -1 ? null : idx;
        };
        const positionColLocal = findLocalCol((t) => t.includes("position"));
        const lColLocal = findLocalCol((t) => t === "l*" || t === "l");
        const aColLocal = findLocalCol((t) => t === "a*" || t === "a");
        const bColLocal = findLocalCol((t) => t === "b*" || t === "b");

        const dataRows = existingRows.slice(2);

        const setCellValue = (row: any, cellIdx: number, rawVal: any) => {
          const cellNode = row.content?.[cellIdx];
          if (!cellNode) return;
          if (rawVal === undefined || rawVal === null || rawVal === "") {
            cellNode.content = [{ type: "paragraph", content: [] }];
            return;
          }
          const num = Number(rawVal);
          const text = !isNaN(num) ? num.toFixed(2) : String(rawVal);
          cellNode.content = [
            { type: "paragraph", content: [{ type: "text", text }] },
          ];
        };

        const updatedDataRows = dataRows.map((row: any, rowIdx: number) => {
          const newRow = JSON.parse(JSON.stringify(row));

          for (let g = 0; g < numGroups; g++) {
            const groupStart = g * columnsPerGroup;
            const apiItem = rawItems[g];
            if (!apiItem) continue;

            // Use the row's own existing Position label (e.g. "1".."10",
            // "AVG", "STDEV") when present — these already come pre-filled
            // from the template — otherwise fall back to the row's order.
            let positionLabel =
              positionColLocal !== null
                ? extractTextFromNode(
                    row.content?.[groupStart + positionColLocal] || {},
                  ).trim()
                : "";
            if (!positionLabel) {
              if (rowIdx < 10) positionLabel = String(rowIdx + 1);
              else if (rowIdx === 10) positionLabel = "AVG";
              else if (rowIdx === 11) positionLabel = "STDEV";
            }

            const measurements = Array.isArray(apiItem.measurements)
              ? apiItem.measurements
              : [];
            const match = measurements.find(
              (m: any) =>
                String(m?.position ?? "")
                  .trim()
                  .toUpperCase() === positionLabel.toUpperCase(),
            );

            if (match) {
              if (lColLocal !== null)
                setCellValue(newRow, groupStart + lColLocal, match.L);
              if (aColLocal !== null)
                setCellValue(newRow, groupStart + aColLocal, match.a);
              if (bColLocal !== null)
                setCellValue(newRow, groupStart + bColLocal, match.b);
            }
          }

          return newRow;
        });

        const completeTableNode = {
          type: "table",
          content: [superHeaderRow, subHeaderRow, ...updatedDataRows],
        };

        editor
          .chain()
          .focus()
          .insertContentAt(
            { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
            completeTableNode,
          )
          .run();
        setLoading(false);
        return;
      }

      // ─────────────────────────────────────────────────────────────
      // STANDARD LOGIC FALLBACK
      // ─────────────────────────────────────────────────────────────
      const headerRows: any[] = [];
      const footerRows: any[] = [];
      let templateInstructionTexts: string[] = [];

      const nonFooterRows = existingRows.filter((row: any) => {
        const combinedText = getRowTexts(row).join(" ").trim().toUpperCase();
        if (
          combinedText.includes("AVERAGE") ||
          combinedText.includes("SUMMARY") ||
          combinedText.includes("TOTAL") ||
          combinedText.includes("STDV")
        ) {
          footerRows.push(row);
          return false;
        }
        return true;
      });

      const row0CellCount = nonFooterRows[0]?.content?.length || 0;
      const row1CellCount = nonFooterRows[1]?.content?.length || 0;
      let columnHeaderRowIdx = 0;

      if (row0CellCount === 1 && row1CellCount > 1) {
        columnHeaderRowIdx = 1;
      }

      for (let i = 0; i <= columnHeaderRowIdx; i++) {
        if (nonFooterRows[i]) headerRows.push(nonFooterRows[i]);
      }

      const dummyRow = nonFooterRows[columnHeaderRowIdx + 1];
      if (dummyRow) {
        templateInstructionTexts = getRowTexts(dummyRow);
      }

      const columnHeaderRow = headerRows[columnHeaderRowIdx] || headerRows[0];
      let columnHeaderTexts = getRowTexts(columnHeaderRow);

      if (
        columnHeaderTexts.every((text) => !text.trim()) &&
        columnHeaderRowIdx > 0
      ) {
        columnHeaderTexts = getRowTexts(headerRows[0]);
      }

      const columnCount = columnHeaderRow?.content?.length || 1;

      const generatedDataRows = rawItems.map((apiItem: any) => {
        const cells = Array.from({ length: columnCount }).map((_, colIndex) => {
          const headerText = columnHeaderTexts[colIndex] || "";
          const instructionText = templateInstructionTexts[colIndex] || "";

          const originalCellAttrs = columnHeaderRow?.content?.[colIndex]
            ?.attrs || {
            colspan: 1,
            rowspan: 1,
            colwidth: null,
          };

          const { isImage, value, rawImages, lines } = getDynamicCellValue(
            headerText,
            apiItem,
            colIndex,
          );

          if (isImage && rawImages.length > 0) {
            const imageParagraphNodes = rawImages.map((imgUrl) => ({
              type: "paragraph",
              content: [
                {
                  type: "image",
                  attrs: {
                    src: imgUrl,
                    alt: "Cell Image",
                    title: null,
                    width: 300,
                    height: 200,
                  },
                },
              ],
            }));
            return {
              type: "tableCell",
              attrs: originalCellAttrs,
              content: imageParagraphNodes,
            };
          }

          // Arrays of measurement objects (e.g. multiple color readings per
          // module) are exposed as `lines` — one already-formatted summary
          // per reading — instead of a single joined string. Render each as
          // its own paragraph so the cell shows readable rows instead of
          // "[object Object]" repeated once per entry.
          if (lines && lines.length > 0) {
            return {
              type: "tableCell",
              attrs: originalCellAttrs,
              content: lines.map((line) => ({
                type: "paragraph",
                content: [{ type: "text", text: line }],
              })),
            };
          }

          let finalCellText = value;
          if (
            !finalCellText ||
            finalCellText === "N/A" ||
            finalCellText === "null"
          ) {
            if (instructionText.includes("If NONE, write")) {
              const match = instructionText.match(/“([^”]+)”|"([^"]+)"/);
              finalCellText = match ? match[1] || match[2] : "None";
            } else {
              finalCellText = "None";
            }
          }

          return {
            type: "tableCell",
            attrs: originalCellAttrs,
            content: [
              {
                type: "paragraph",
                content: finalCellText
                  ? [{ type: "text", text: finalCellText }]
                  : [],
              },
            ],
          };
        });

        return { type: "tableRow", content: cells };
      });

      const completeTableNode = {
        type: "table",
        content: [...headerRows, ...generatedDataRows, ...footerRows],
      };

      editor
        .chain()
        .focus()
        .insertContentAt(
          { from: nextNodePos, to: nextNodePos + nextNode.nodeSize },
          completeTableNode,
        )
        .run();
    } catch (error) {
      console.error("TIPTAP SCHEMA OR BINDING ERROR:", error);
      alert(error instanceof Error ? error.message : "Unable to bind report data.");
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  const getJustifyContent = () => {
    if (alignment === "center") return "center";
    if (alignment === "left") return "flex-start";
    return "flex-end";
  };

  if (!node) return null;

  return (
    <NodeViewWrapper
      as="div"
      className="lsdb-button-node"
      contentEditable={false}
      style={{
        display: "flex",
        justifyContent: getJustifyContent(),
        alignItems: "center",
        width: "100%",
      }}
    >
      <div
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: loading ? "not-allowed" : "pointer",
        }}
      >
        <Tooltip
          title={
            loading ? "Fetching data from LSDB..." : "Fetch data from LSDB"
          }
        >
          <IconButton size="small" color="primary" disabled={loading}>
            {loading ? (
              <img
                src="/loader.gif"
                alt="Fetch from LSDB"
                style={{
                  width: 24,
                  height: 24,
                  display: "block",
                  objectFit: "contain",
                }}
              />
            ) : (
              <img
                src="/fetch_lsdb.png"
                alt="Fetch from LSDB"
                style={{
                  width: 24,
                  height: 24,
                  display: "block",
                  objectFit: "contain",
                }}
              />
            )}
          </IconButton>
        </Tooltip>
      </div>
      {/* <div
        onClick={handleClick}
        style={{
          display: "inline-flex",
          alignItems: "center",
          border: loading ? "1px solid #FF8C00" : "1px solid #FFA500",
          borderRadius: 4,
          padding: "2px 8px",
          cursor: loading ? "not-allowed" : "pointer",
          gap: 6,
          backgroundColor: loading ? "#FFF8ED" : "#ffffff",
          transition: "all 0.2s ease-in-out",
        }}
      >
        <Tooltip title={loading ? "Fetching data from LSDB..." : "Pull Data from LSDB"}>
          <IconButton size="small" color="primary" style={{ padding: 4 }} disabled={loading}>
            {loading ? (
              <CircularProgress size={16} style={{ color: "#FF8C00" }} />
            ) : (
              <RefreshIcon fontSize="small" style={{ color: "#1976D2" }} />
            )}
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          style={{
            userSelect: "none",
            color: loading ? "#D97706" : "#1976D2",
            fontWeight: loading ? 600 : 400,
          }}
        >
          {loading ? "Fetching Data..." : "Pull Data from LSDB"}
        </Typography>
      </div> */}
    </NodeViewWrapper>
  );
}
